import React, { memo, ReactNode, useContext, useMemo } from "react";
import { withStyles } from "@swiggy-private/core-ui";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/24";
import { default as CloseIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/close/20";
import classNames from "classnames";

// dls
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";

// style
import { HeaderContentWrapper, IconContainer } from "../Header.style";
import styles from "../Header.scss";

// context
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// helpers
import { isQuickCheckoutFlow } from "@cart-client/helpers/QuickCheckoutCart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

const HeaderContent = ({
    onBackButtonClick,
    children,
    isUserBlackMember = false,
    isGiftingCartFlow = false,
    showAddressWidgetTop = false,
    isJioHotstarFlow = false,
}: {
    onBackButtonClick: VoidFunction;
    children: ReactNode;
    isUserBlackMember?: boolean;
    isGiftingCartFlow?: boolean;
    showAddressWidgetTop?: boolean;
    isJioHotstarFlow?: boolean;
}) => {
    const { cartHeaderState } = useContext(CartHeaderContext);
    const { cartHeaderLottieState } = useContext(CartHeaderLottieContext);
    const isQuickCheckout = useMemo(() => isQuickCheckoutFlow(), []);

    // Determine the stroke and fill color based on the theme and environment
    const is99Store = cartHeaderLottieState.cartType === LottieCartType._99STORE;
    const isThemeChanged = (cartHeaderState.themeChange || cartHeaderLottieState.themeChange) && !is99Store;
    const isThemeApplied = isThemeChanged || isUserBlackMember || cartHeaderLottieState.themeChange;
    const isStaging = typeof window !== "undefined" && window.__ENVIRONMENT__ === "staging";
    const isStaging2 = typeof window !== "undefined" && window.__ENVIRONMENT__ === "staging-2";
    const iconColor = useMemo(() => {
        if (isStaging) {
            return "rain";
        } else if (isStaging2) {
            return "warning";
        } else if (isThemeChanged) {
            return "text_inverse_Med_Emphasis";
        } else {
            return "text_Med_Emphasis";
        }
    }, [isStaging, isStaging2, isThemeChanged]);

    const headerContainerClassName = classNames(styles.containerContent, {
        [styles.containerContentGifting]: isGiftingCartFlow,
    });

    const [BackButtonIcon, BackButtonTestId] = useMemo(() => {
        if (isQuickCheckout) {
            return [CloseIcon, COMMON_HEADER_TEST_IDS.CLOSE_BUTTON];
        }
        return [ArrowBack, COMMON_HEADER_TEST_IDS.BACK_BUTTON];
    }, [isQuickCheckout]);

    return (
        <HeaderContentWrapper
            isThemeChange={isThemeApplied || isGiftingCartFlow}
            isGiftingCartFlow={isGiftingCartFlow}
            showAddressWidgetTop={showAddressWidgetTop}
            isJioHotstarFlow={isJioHotstarFlow}
            isMroFlow={isMROFlow()}
        >
            <IconContainer isQuickCheckout={isQuickCheckout}>
                <GenericIcon
                    Icon={BackButtonIcon}
                    onClick={onBackButtonClick}
                    strokeColor={iconColor}
                    fillColor={iconColor}
                    data-testid={BackButtonTestId}
                />
            </IconContainer>
            <div className={headerContainerClassName}>{children}</div>
        </HeaderContentWrapper>
    );
};

export default memo(withStyles(HeaderContent, styles));
